import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  HelpCircle, Search, ChevronDown, ChevronUp, Shield, CreditCard, Car,
  MessageSquare, FileCheck, AlertTriangle, Lock, Users, Phone, Mail,
  ShieldCheck, Bot, Eye, Fingerprint
} from 'lucide-react';

const FAQ_CATEGORIES = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: Car,
    questions: [
      {
        q: 'How do I create an account?',
        a: 'You can create an account by clicking "Get Started" and signing up with your email or Google account. Registration is free and takes less than a minute.'
      },
      {
        q: 'Is AutoTrust Ghana free to use?',
        a: 'Yes! Listing your car and browsing listings is completely free. We only charge a small fee when a transaction is completed through our escrow system.'
      },
      {
        q: 'What areas does AutoTrust cover?',
        a: 'AutoTrust operates across all major cities in Ghana including Accra, Kumasi, Tamale, Cape Coast, Takoradi, Tema, Ho, Sunyani, Bolgatanga, and Koforidua.'
      },
      {
        q: 'How is AutoTrust different from other car marketplaces?',
        a: 'AutoTrust uses AI-powered verification to check every listing. We verify VINs, analyze photos for authenticity, validate prices against market data, and offer secure escrow payments. This means zero scams and complete peace of mind.'
      }
    ]
  },
  {
    id: 'buying',
    title: 'Buying a Car',
    icon: ShieldCheck,
    questions: [
      {
        q: 'How do I know if a listing is trustworthy?',
        a: 'Every listing has a Trust Score (0-100) based on three factors: VIN verification, image authenticity, and price validation. Listings with scores above 80 are highly trustworthy. Look for the green checkmarks next to each verification item.'
      },
      {
        q: 'What is the escrow payment system?',
        a: 'When you buy with escrow, your payment is held securely by our payment partner (Flutterwave) until you receive and inspect the vehicle. Once you confirm everything is satisfactory, the funds are released to the seller. This protects you from fraud.'
      },
      {
        q: 'Can I contact the seller before buying?',
        a: 'Absolutely! Use the "Contact Seller" button on any listing to start a secure in-app conversation. You never need to share your personal phone number.'
      },
      {
        q: 'What if the car is not as described?',
        a: 'If you used escrow payment and the car doesn\'t match the listing, do not confirm delivery. Instead, open a dispute through your dashboard. Our AI-powered dispute resolution system will analyze your case and help resolve it fairly.'
      },
      {
        q: 'How do I check a car\'s history?',
        a: 'Every listing shows VIN (Vehicle Identification Number) verification results, including the car\'s make, model, year, and country of origin. For detailed reports, the VIN is decoded using NHTSA data.'
      }
    ]
  },
  {
    id: 'selling',
    title: 'Selling Your Car',
    icon: Car,
    questions: [
      {
        q: 'How do I list my car?',
        a: 'Click "Sell Your Car" and follow our 3-step process: 1) Enter your VIN for automatic verification, 2) Upload clear photos (our AI checks quality), 3) Set your price and publish. It takes about 5 minutes.'
      },
      {
        q: 'What photos should I include?',
        a: 'Include clear photos of: exterior (front, back, sides), interior (dashboard, seats), engine, and any damage or wear. Our AI will check image quality and flag any issues. Avoid photos with phone numbers or text overlays.'
      },
      {
        q: 'How do I price my car correctly?',
        a: 'When you create a listing, our AI-powered pricing tool estimates your car\'s market value based on current Ghana marketplace data. This helps you set a competitive, fair price.'
      },
      {
        q: 'When do I receive payment?',
        a: 'If the buyer uses escrow, payment is released to your account once they confirm delivery. This typically happens within 24-48 hours of confirmation. You can also sell outside escrow, but we recommend using it for security.'
      },
      {
        q: 'How do I mark my car as sold?',
        a: 'Go to your Dashboard, find the listing, and click the "Mark as Sold" button. You can optionally add buyer details and sale notes. The listing will then show a SOLD badge.'
      }
    ]
  },
  {
    id: 'escrow',
    title: 'Escrow & Payments',
    icon: CreditCard,
    questions: [
      {
        q: 'How does escrow protect me?',
        a: 'Escrow acts as a trusted middleman. Buyers\' funds are held securely until the car is delivered and inspected. Sellers know the buyer has real money. Both parties are protected from fraud.'
      },
      {
        q: 'What payment methods are accepted?',
        a: 'We accept card payments (Visa, Mastercard), bank transfers, and mobile money through Flutterwave. All payments are processed securely with bank-level encryption.'
      },
      {
        q: 'Are there any fees?',
        a: 'Listing is free. When you complete a transaction through escrow, there\'s a small service fee (typically 1-2%) to cover payment processing and platform costs.'
      },
      {
        q: 'How long does the escrow process take?',
        a: 'Once the buyer pays, funds are held in escrow. After the buyer confirms delivery (inspects the car and is satisfied), funds are released to the seller within 24-48 hours.'
      },
      {
        q: 'What happens if there\'s a dispute?',
        a: 'Our AI-powered dispute resolution system analyzes the case and recommends a fair resolution. Options include full refund, partial refund, or completing the transaction. Complex cases are escalated to our support team.'
      }
    ]
  },
  {
    id: 'verification',
    title: 'Verification & Trust',
    icon: FileCheck,
    questions: [
      {
        q: 'What is KYC verification?',
        a: 'KYC (Know Your Customer) verification confirms a seller\'s identity using their Ghana Card, Driver\'s License, or Passport. Verified sellers display a badge, giving buyers extra confidence.'
      },
      {
        q: 'How does VIN verification work?',
        a: 'When you enter a VIN, our system decodes it to reveal the car\'s manufacturer, model, year, and country of origin. We cross-reference with NHTSA data for additional details like body type, engine, and plant location.'
      },
      {
        q: 'What does the AI image check detect?',
        a: 'Our computer vision AI checks for: image quality (blur, lighting), hidden contact information (phone numbers), edited license plates, signs of photo manipulation, and whether the image actually shows a car.'
      },
      {
        q: 'How is the Trust Score calculated?',
        a: 'Trust Score (0-100) is based on: VIN validity (40 points), image authenticity (25 points), price fairness vs. market data (30 points), and listing completeness (5 points).'
      }
    ]
  },
  {
    id: 'safety',
    title: 'Safety & Security',
    icon: AlertTriangle,
    questions: [
      {
        q: 'How do I avoid scams?',
        a: 'Always: 1) Use escrow payments, 2) Check the Trust Score, 3) Verify seller KYC status, 4) Use in-app messaging only, 5) Never send money outside the platform, 6) Meet in safe, public places for inspection.'
      },
      {
        q: 'What should I do if I suspect fraud?',
        a: 'Report the listing immediately using the "Report" button. Do not proceed with any payment. If you\'ve already paid through escrow, open a dispute through your dashboard before confirming delivery.'
      },
      {
        q: 'Is my personal information safe?',
        a: 'Yes. We use industry-standard encryption, never share your data with third parties without consent, and comply with data protection regulations. See our Privacy Policy for details.'
      },
      {
        q: 'Can sellers see my personal details?',
        a: 'Sellers only see your name and in-app messages. Your email, phone number, and payment details are never shared with sellers.'
      }
    ]
  },
  {
    id: 'support',
    title: 'Support & Contact',
    icon: MessageSquare,
    questions: [
      {
        q: 'How do I contact support?',
        a: 'Use our AI chatbot (bottom right corner) for instant help. For complex issues, email support@autotrustgh.com or reach out through our contact form.'
      },
      {
        q: 'What are your support hours?',
        a: 'Our AI chatbot is available 24/7. Human support is available Monday-Saturday, 8am-6pm GMT.'
      },
      {
        q: 'How do I report a problem with a listing?',
        a: 'Click the "Report" button on any listing page. Describe the issue and our team will investigate within 24 hours.'
      }
    ]
  }
];

export default function Help() {
  const [searchQuery, setSearchQuery] = useState('');
  const [openCategory, setOpenCategory] = useState('getting-started');
  const [openQuestions, setOpenQuestions] = useState({});

  const toggleQuestion = (categoryId, questionIndex) => {
    const key = `${categoryId}-${questionIndex}`;
    setOpenQuestions(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const filteredCategories = FAQ_CATEGORIES.map(category => ({
    ...category,
    questions: category.questions.filter(
      q => q.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
           q.a.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category => category.questions.length > 0);

  return (
    <main data-testid="help-page" className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-card to-background py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="w-16 h-16 rounded-full bg-gold/10 flex items-center justify-center mx-auto mb-6">
              <HelpCircle className="w-8 h-8 text-gold" />
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              How Can We Help?
            </h1>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto mb-8">
              Find answers to common questions about buying, selling, and staying safe on AutoTrust Ghana.
            </p>
            
            {/* Search Bar */}
            <div className="relative max-w-xl mx-auto">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search for help..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-14 text-lg bg-background border-border/50"
                data-testid="help-search"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-12 border-b border-border/50">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: Bot, title: 'AI Assistant', desc: 'Chat with our AI', link: '#', action: 'chatbot' },
              { icon: Shield, title: 'Trust & Safety', desc: 'How we protect you', link: '#safety' },
              { icon: CreditCard, title: 'Escrow Guide', desc: 'Secure payments', link: '#escrow' },
              { icon: FileCheck, title: 'Verification', desc: 'KYC & VIN checks', link: '#verification' },
            ].map((item, i) => (
              <motion.a
                key={i}
                href={item.link}
                className="bg-card border border-border/50 rounded-xl p-5 hover:border-gold/30 transition-colors group"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
              >
                <item.icon className="w-8 h-8 text-gold mb-3 group-hover:scale-110 transition-transform" />
                <h3 className="font-semibold mb-1">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <h2 className="text-2xl font-bold mb-8">Frequently Asked Questions</h2>
          
          {/* Category Navigation */}
          <div className="flex flex-wrap gap-2 mb-8">
            {FAQ_CATEGORIES.map(category => (
              <button
                key={category.id}
                onClick={() => setOpenCategory(category.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  openCategory === category.id
                    ? 'bg-gold text-black'
                    : 'bg-card border border-border/50 hover:border-gold/30'
                }`}
              >
                {category.title}
              </button>
            ))}
          </div>

          {/* Questions */}
          <div className="space-y-4">
            {(searchQuery ? filteredCategories : FAQ_CATEGORIES.filter(c => c.id === openCategory)).map(category => (
              <div key={category.id} id={category.id}>
                {searchQuery && (
                  <div className="flex items-center gap-2 mb-4">
                    <category.icon className="w-5 h-5 text-gold" />
                    <h3 className="font-semibold">{category.title}</h3>
                  </div>
                )}
                <div className="space-y-3">
                  {category.questions.map((item, i) => {
                    const key = `${category.id}-${i}`;
                    const isOpen = openQuestions[key];
                    return (
                      <div
                        key={i}
                        className="bg-card border border-border/50 rounded-xl overflow-hidden"
                      >
                        <button
                          onClick={() => toggleQuestion(category.id, i)}
                          className="w-full flex items-center justify-between p-5 text-left hover:bg-background/50 transition-colors"
                          data-testid={`faq-${category.id}-${i}`}
                        >
                          <span className="font-medium pr-4">{item.q}</span>
                          {isOpen ? (
                            <ChevronUp className="w-5 h-5 text-gold flex-shrink-0" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                          )}
                        </button>
                        {isOpen && (
                          <div className="px-5 pb-5 text-muted-foreground leading-relaxed border-t border-border/50 pt-4">
                            {item.a}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-card/50 border-t border-border/50">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-2xl font-bold mb-4">Still Need Help?</h2>
          <p className="text-muted-foreground mb-8">
            Our support team is here to assist you with any questions.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="mailto:support@autotrustgh.com">
              <Button variant="outline" className="gap-2 h-12 px-6">
                <Mail className="w-4 h-4" /> Email Support
              </Button>
            </a>
            <a href="tel:+233000000000">
              <Button variant="outline" className="gap-2 h-12 px-6">
                <Phone className="w-4 h-4" /> Call Us
              </Button>
            </a>
          </div>
          <p className="text-sm text-muted-foreground mt-6">
            Support hours: Monday-Saturday, 8am-6pm GMT
          </p>
        </div>
      </section>
    </main>
  );
}
